import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiStar, FiMessageSquare, FiCheckCircle, FiClock, FiFilter,
  FiSearch, FiRefreshCw, FiTrendingUp, FiUsers, FiBarChart2,
  FiEdit, FiArchive, FiEye, FiX
} from 'react-icons/fi';

function FeedbackManagement() {
  const { alert } = useModal();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    guest_type: '',
    min_rating: '',
    search: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [feedbackDelay, setFeedbackDelay] = useState(30);
  const [loadingDelay, setLoadingDelay] = useState(false);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [activeTab, setActiveTab] = useState('bill_qr');

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.guest_type) params.append('guest_type', filters.guest_type);
      if (filters.min_rating) params.append('min_rating', filters.min_rating);

      const response = await api.get(`/api/feedback?${params.toString()}`);
      const feedbackData = response.data || [];
      setFeedback(feedbackData);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      setFeedback([]);
      // Don't show alert on initial load if it's a 404 or empty response
      if (error.response?.status !== 404 && error.response?.status !== 403 && !initialLoad) {
        await alert('Failed to fetch feedback: ' + (error.response?.data?.error || error.message), 'Error', 'error');
      }
      // If 403, user doesn't have permission - this shouldn't happen for admin
      if (error.response?.status === 403) {
        console.error('Permission denied - user may not have access to feedback');
      }
    } finally {
      setLoading(false);
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await api.get('/api/feedback/analytics');
      setAnalytics(response.data || null);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAnalytics(null);
      // Don't show alert for analytics errors
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      await alert('Please enter a response', 'Validation Error', 'warning');
      return;
    }

    try {
      setSubmittingResponse(true);
      await api.patch(`/api/feedback/${selectedFeedback.id}/respond`, {
        admin_response: responseText
      });
      
      setShowResponseModal(false);
      setResponseText('');
      setSelectedFeedback(null);
      fetchFeedback();
      fetchAnalytics();
      await alert('Response sent successfully!', 'Success', 'success');
    } catch (error) {
      console.error('Failed to respond:', error);
      await alert(error.response?.data?.error || 'Failed to send response', 'Error', 'error');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await api.patch(`/api/feedback/${feedbackId}/status`, { status: newStatus });
      fetchFeedback();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to update status:', error);
      await alert('Failed to update status', 'Error', 'error');
    }
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400">No rating</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`h-4 w-4 ${
              star <= Math.round(rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-semibold text-gray-700">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'responded': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubmissionStatus = (item) => {
    const hasRatings = [item.food_rating, item.service_rating, item.ambiance_rating, item.overall_rating]
      .some((value) => value !== null && value !== undefined);
    const hasText = Boolean(item.comments?.trim() || item.suggestions?.trim());

    return (hasRatings || hasText) ? 'given' : 'not_given';
  };

  const getSubmissionStatusColor = (submissionStatus) => {
    return submissionStatus === 'given'
      ? 'bg-green-100 text-green-800'
      : 'bg-yellow-100 text-yellow-800';
  };

  const fetchFeedbackDelay = async () => {
    try {
      const response = await api.get('/api/feedback/delay');
      setFeedbackDelay(response.data?.delayMinutes || 30);
    } catch (error) {
      console.error('Failed to fetch feedback delay:', error);
    }
  };

  const updateFeedbackDelay = async (newDelay) => {
    try {
      setLoadingDelay(true);
      await api.put('/api/feedback/delay', { delayMinutes: newDelay });
      setFeedbackDelay(newDelay);
      setShowDelayModal(false);
      await alert('Feedback delay updated successfully!', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update feedback delay', 'Error', 'error');
    } finally {
      setLoadingDelay(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchFeedback(), fetchAnalytics(), fetchFeedbackDelay()]);
      } catch (error) {
        console.error('Error loading feedback data:', error);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change (except search which is client-side)
  useEffect(() => {
    if (!initialLoad) {
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.guest_type, filters.min_rating]);

  // Client-side search filter - must be before conditional return (React Hooks rule)
  const filteredFeedback = React.useMemo(() => {
    const sourceFiltered = feedback.filter((f) => {
      if (activeTab === 'whatsapp') {
        return f.submitted_via === 'whatsapp';
      }
      return f.submitted_via !== 'whatsapp';
    });

    if (!filters.search) return sourceFiltered;

    const searchLower = filters.search.toLowerCase();
    return sourceFiltered.filter((f) =>
      f.comments?.toLowerCase().includes(searchLower) ||
      f.suggestions?.toLowerCase().includes(searchLower) ||
      f.restaurant_guests?.name?.toLowerCase().includes(searchLower) ||
      f.restaurant_guests?.phone?.includes(searchLower)
    );
  }, [feedback, filters.search, activeTab]);

  if (loading && initialLoad) {
    return (
      <Layout>
        <div className="bg-white rounded-lg border-0 shadow-sm p-16 text-center">
          <div className="animate-pulse">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 font-medium">Loading feedback...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Feedback Management</h1>
              <p className="text-sm text-gray-600">View and manage customer feedback</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDelayModal(true)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm transition-colors"
                title="Configure feedback delay"
              >
                <FiClock className="h-4 w-4" />
                <span>Delay: {feedbackDelay} min</span>
              </button>
              <button
                onClick={() => {
                  fetchFeedback();
                  fetchAnalytics();
                }}
                disabled={loading}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="mt-4 inline-flex p-1 bg-gray-100 rounded-lg gap-1">
            <button
              onClick={() => setActiveTab('bill_qr')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'bill_qr'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Bill QR Feedback
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'whatsapp'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              WhatsApp Feedback
            </button>
          </div>
        </div>

        {/* Analytics Cards */}
        {loadingAnalytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg border-0 shadow-sm p-4 animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-20 mb-3"></div>
                <div className="h-6 bg-gray-200 rounded w-12"></div>
              </div>
            ))}
          </div>
        ) : analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-600">Total Feedback</h3>
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <FiMessageSquare className="h-4 w-4 text-gray-600" />
                </div>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.total_feedback}</p>
              <p className="text-xs text-gray-500 mt-1">All time</p>
            </div>

            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-600">Average Rating</h3>
                <div className="p-1.5 bg-yellow-100 rounded-lg">
                  <FiStar className="h-4 w-4 text-yellow-500 fill-current" />
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-xl font-bold text-gray-900">
                  {analytics.average_ratings?.overall?.toFixed(1) || '0.0'}
                </p>
                <span className="text-xs text-gray-500">/ 5.0</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Overall satisfaction</p>
            </div>

            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-600">Response Rate</h3>
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <FiTrendingUp className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.response_rate?.toFixed(1) || '0.0'}%</p>
              <p className="text-xs text-gray-500 mt-1">Feedback responded to</p>
            </div>

            <div className="bg-white rounded-lg border-0 shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-600">With Ratings</h3>
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <FiBarChart2 className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.with_ratings}</p>
              <p className="text-xs text-gray-500 mt-1">Rated feedback</p>
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm bg-white"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="responded">Responded</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Guest Type</label>
              <select
                value={filters.guest_type}
                onChange={(e) => setFilters({ ...filters, guest_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm bg-white"
              >
                <option value="">All Types</option>
                <option value="restaurant">Restaurant</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Min Rating</label>
              <select
                value={filters.min_rating}
                onChange={(e) => setFilters({ ...filters, min_rating: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm bg-white"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
                <option value="1">1+ Star</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search comments..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Feedback List */}
        <div className="bg-white rounded-lg border-0 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">All Feedback</h2>
              <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                {filteredFeedback.length} {filteredFeedback.length === 1 ? 'item' : 'items'}
              </span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="text-center py-12">
                <FiRefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium text-sm">Loading feedback...</p>
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <FiMessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium mb-1">No feedback found</p>
                <p className="text-xs text-gray-400">Try adjusting your filters</p>
              </div>
            ) : (
              filteredFeedback.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      {(() => {
                        const submissionStatus = getSubmissionStatus(item);
                        return (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getSubmissionStatusColor(submissionStatus)}`}>
                          {submissionStatus === 'given' ? 'Given' : 'Not Given'}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                          Admin: {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <FiClock className="h-3 w-3" />
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${item.submitted_via === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {item.submitted_via === 'whatsapp' ? 'WhatsApp' : 'Bill QR'}
                        </span>
                        {item.guest_type && (
                          <span className="text-xs text-gray-500 capitalize px-2 py-0.5 bg-gray-100 rounded">
                            {item.guest_type} Guest
                          </span>
                        )}
                        {item.orders && (
                          <span className="text-xs text-gray-500 px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            Order: {item.orders.order_number}
                          </span>
                        )}
                        {item.reservations && (
                          <span className="text-xs text-gray-500 px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                            Reservation: {item.reservations.reservation_number}
                          </span>
                        )}
                      </div>
                        );
                      })()}

                      {(item.restaurant_guests?.name || item.restaurant_guests?.phone) && (
                        <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          <span className="font-medium">Guest:</span>{' '}
                          {item.restaurant_guests?.name || 'WhatsApp Customer'}
                          {item.restaurant_guests?.phone ? ` • ${item.restaurant_guests.phone}` : ''}
                        </div>
                      )}

                      {/* Ratings */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Food</p>
                          {renderStars(item.food_rating)}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Service</p>
                          {renderStars(item.service_rating)}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Ambiance</p>
                          {renderStars(item.ambiance_rating)}
                        </div>
                      </div>

                      {/* Comments */}
                      {item.comments && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1.5">
                            <FiMessageSquare className="h-3 w-3" />
                            Comments
                          </p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.comments}</p>
                        </div>
                      )}

                      {/* Suggestions */}
                      {item.suggestions && (
                        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                          <p className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1.5">
                            <FiEdit className="h-3 w-3" />
                            Suggestions
                          </p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.suggestions}</p>
                        </div>
                      )}

                      {/* Admin Response */}
                      {item.admin_response && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-xs font-semibold text-green-900 flex items-center gap-1.5">
                              <FiCheckCircle className="h-3 w-3" />
                              Admin Response
                            </p>
                            {item.users && (
                              <p className="text-xs text-green-600">
                                By {item.users.name} • {new Date(item.responded_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <p className="text-sm text-green-800 whitespace-pre-wrap">{item.admin_response}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 min-w-[120px]">
                      {!item.admin_response && (
                        <button
                          onClick={() => {
                            setSelectedFeedback(item);
                            setShowResponseModal(true);
                          }}
                          className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 text-xs font-medium transition-colors"
                        >
                          <FiEdit className="h-3 w-3" />
                          Respond
                        </button>
                      )}
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        className="px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="responded">Responded</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Response Modal */}
        {showResponseModal && selectedFeedback && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
            setShowResponseModal(false);
            setSelectedFeedback(null);
            setResponseText('');
          }}>
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Respond to Feedback</h2>
                <button
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedFeedback(null);
                    setResponseText('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Original Feedback:</p>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {selectedFeedback.comments ? (
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.comments}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No comments provided</p>
                    )}
                    {selectedFeedback.suggestions && (
                      <div className="mt-2 pt-2 border-t border-gray-300">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Suggestions:</p>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap">{selectedFeedback.suggestions}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Your Response</label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm resize-none"
                    placeholder="Enter your response to the customer..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleRespond}
                    disabled={submittingResponse || !responseText.trim()}
                    className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm transition-colors"
                  >
                    {submittingResponse ? (
                      <>
                        <FiRefreshCw className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="h-4 w-4" /> Send Response
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowResponseModal(false);
                      setSelectedFeedback(null);
                      setResponseText('');
                    }}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Delay Modal */}
        {showDelayModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDelayModal(false)}>
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Configure Feedback Delay</h2>
                <button
                  onClick={() => setShowDelayModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Delay After Menu Request (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1440"
                    value={feedbackDelay}
                    onChange={(e) => setFeedbackDelay(parseInt(e.target.value) || 30)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    placeholder="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Send feedback request X minutes after customer requests menu via WhatsApp (1-1440 minutes)
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => updateFeedbackDelay(feedbackDelay)}
                    disabled={loadingDelay}
                    className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm transition-colors"
                  >
                    {loadingDelay ? (
                      <>
                        <FiRefreshCw className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="h-4 w-4" /> Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowDelayModal(false)}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default FeedbackManagement;

